/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import type { ReactMarkdownProps } from 'react-markdown';

import { typography2 } from '@coursera/cds-core';
import type { Product } from '@coursera/event-pulse-types';
import type { EventData } from '@coursera/event-pulse/react';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { MESSAGE_SENDER } from 'bundles/ai-course-coach/components/constants';
import type { MessageTypes } from 'bundles/ai-course-coach/store/types';
import { useCoachTrackingContext } from 'bundles/ai-course-coach/utils/tracking';

const COACH_PRODUCT_REC_PARAM = 'coach-product-recs';

type Props = {
  message: MessageTypes;
  renderers?: ReactMarkdownProps['renderers'];
};

const styles = {
  root: css`
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    p {
      white-space: pre-wrap;
    }

    p,
    li,
    span {
      ${typography2.bodyPrimary}
    }

    pre {
      border: 1px solid var(--cds-color-neutral-stroke-primary);
      display: block;
      padding: var(--cds-spacing-100);
      margin: 0 0 var(--cds-spacing-100);
      font-size: var(--cds-font-size-150);
      line-height: var(--cds-line-height-300);
      word-break: break-all;
      word-wrap: break-word;
      background-color: var(--cds-color-emphasis-quaternary-background-xweak);
      color: var(--cds-color-neutral-primary);
      border-radius: var(--cds-border-radius-50);
      overflow-x: auto;

      code {
        padding: 0;
        border-radius: 0;
      }
    }

    code {
      padding: var(--cds-spacing-50);
      font-size: var(--cds-font-size-175);
      color: inherit;
      white-space: unset;
      background-color: var(--cds-color-emphasis-quaternary-background-xweak);
      border-radius: var(--cds-border-radius-50);
    }

    img {
      width: 100%;
      -o-object-fit: contain;
      object-fit: contain;
    }
  `,
  markdown: css`
    max-width: 100%;
  `,
};

const isContentRecLink = (linkHref: URL) => {
  return linkHref.searchParams.get('linkType') === COACH_PRODUCT_REC_PARAM;
};

const ParagraphRenderer = (props: React.HTMLAttributes<HTMLParagraphElement>) => <p {...props} />;

const ContentRecLinkRenderer = ({ href, children, ...rest }: React.LinkHTMLAttributes<HTMLAnchorElement>) => {
  const linkHref = new URL(href as string);

  const trackingContext = useCoachTrackingContext()?.data;
  const track = useTracker();
  const trackingData: EventData<'view_coach_product_recommendation'> = {
    recommendationType: 'chat_link',
    product: {
      id: linkHref.searchParams.get('productId') as Product['id'],
      type: linkHref.searchParams.get('productType') as Product['type'],
    },
    coach: {
      mode: trackingContext?.mode ?? 'basic',
    },
  };

  const linkVisibilityRef: React.MutableRefObject<HTMLAnchorElement | null> = useVisibilityTracker(
    'view_coach_product_recommendation',
    { ...trackingData }
  );

  const contentRecLinkProps = {
    ref: linkVisibilityRef,
    onClick: () => {
      track('click_coach_product_recommendation', {
        ...trackingData,
      });
    },
  };

  // clean up custom query params before rendering, these are only meant for FE eventing
  linkHref.searchParams.delete('linkType');
  linkHref.searchParams.delete('productType');
  linkHref.searchParams.delete('productId');

  return (
    <a href={linkHref.href} {...contentRecLinkProps} {...rest} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

const LinkRenderer = ({ href, children, ...rest }: React.LinkHTMLAttributes<HTMLAnchorElement>) => {
  let linkHref: URL;

  try {
    linkHref = new URL(href ?? '');
  } catch {
    // handle any invalid links as plain-text
    return <span>{children}</span>;
  }

  if (isContentRecLink(linkHref)) {
    return (
      <ContentRecLinkRenderer {...rest} href={href}>
        {children}
      </ContentRecLinkRenderer>
    );
  }

  return (
    <a href={linkHref.href} {...rest} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

/** We do not support links by reference, which are indicated through box brackets */
const LinkReferenceRenderer: React.LegacyFunctionComponentWithChildren = ({ children }) => {
  return <React.Fragment>[{children}]</React.Fragment>;
};

/**
 * Markdown renderer for Coach messages
 */
const Message = ({ message, renderers }: Props) => {
  const isCoachResponse = message.sender === MESSAGE_SENDER.RESPONSE;

  return (
    <div
      css={styles.root}
      data-testid="coach-message-markdown"
      className={isCoachResponse ? 'coach-response' : 'learner-response'}
    >
      <ReactMarkdown
        css={styles.markdown}
        renderers={{
          link: renderers?.link ?? LinkRenderer,
          linkReference: LinkReferenceRenderer,
          // prevent heading formatting, although we may not need this after Gemini 1.5 [COACH-587]
          heading: ParagraphRenderer,
          paragraph: ParagraphRenderer,
        }}
        source={message.text}
        linkTarget="_blank"
      />
    </div>
  );
};

export default Message;
